/* eslint-disable @typescript-eslint/indent */
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, useParams, useLocation } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Loader } from '@sdir/sds';
import styled from 'styled-components';
import { AttachmentType } from '@sdir/httpclient/lib/clients/aps/application';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import NewApplicationLayout from '../../NewApplicationLayout';
import { routes } from '../../../../constants/routes';
import { Loadable } from '../../../../types';
import { FormAttachmentGroup } from '../../../Molecules';
import { Card } from '../../../Atoms';
import {
  AttachmentGroupChanges,
  useCurrentApplication,
  useSubmitSequence,
} from '../../../../services/hooks';
import {
  QUALIFICATION_URI,
  findQualification,
  getFromStorage,
} from '../../../../helpers/Qualifications';
import { FileViewer } from '../../../Molecules/FileDrop/FileViewer';
import {
  CreateAttachmentGroupTracker,
  SubmitAttachmentUpdatesParams,
  SubmitAttachmentUpdatesTracker,
  buildCreateAttachmentGroupSequence,
  buildSubmitAttachmentUpdatesSequence,
} from '../../../../helpers/attachmentApiHelpers';
import EditApplicationGuard from '../../../Molecules/EditApplicationGuard/EditApplicationGuard';
import { useNavigateWithLang } from '../../../../services/hooks/useNavigateWithLang';

type AddCourseForm = {
  attachments: AttachmentGroupChanges;
};

export const AddCourse: React.FC = () => {
  const fm = useIntl().formatMessage;
  const navigate = useNavigateWithLang();
  const { attachmentGroup, applicationUid } = useParams<{
    attachmentGroup: string;
    applicationUid: string;
  }>();
  const attachmentGroupFormatted = attachmentGroup === 'new' ? null : attachmentGroup || null;
  const location = useLocation();
  const pathname = location?.pathname;
  const search = location?.search;
  const editingExistingQualification = pathname?.includes('/editcourse');
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const uriFromStorage = getFromStorage(QUALIFICATION_URI);
  const fromMissingDocumentation = searchParams.get('from') === 'missingdocumentation';

  const [activeFile, setActiveFile] = useState<Loadable<File> | null>();
  const [submittingCourse, setSubmittingCourse] = useState<boolean>(false);

  const { application, qualifications, refresh } = useCurrentApplication({
    includeQualifications: true,
  });

  const [submitAttachmentsTracker, setSubmitAttachmentsTracker] =
    useState<SubmitAttachmentUpdatesTracker>({});
  const { submit: submitAttachments } = useSubmitSequence<
    SubmitAttachmentUpdatesParams,
    SubmitAttachmentUpdatesTracker
  >(buildSubmitAttachmentUpdatesSequence);
  const [submitAttachmentGroupTracker, setSubmitAttachmentGroupTracker] =
    useState<CreateAttachmentGroupTracker>({});
  const { submit: submitAttachmentGroup } = useSubmitSequence(buildCreateAttachmentGroupSequence);

  const attachmentsForGroup = useMemo(
    () =>
      application.result?.attachments?.filter((a) => a.group === attachmentGroupFormatted) || [],
    [application.result?.attachments, attachmentGroupFormatted]
  );

  const groupQualificationUri =
    attachmentsForGroup?.[0]?.simulatorResultItemUri || uriFromStorage || null;

  const qualification = useMemo(() => {
    if (!qualifications.result || !groupQualificationUri) return null;
    return findQualification(qualifications.result, groupQualificationUri);
  }, [qualifications.result, groupQualificationUri]);

  const handleExit = useCallback(() => {
    const route = fromMissingDocumentation
      ? routes.application.uploadMissingDocumentation
      : routes.application.qualification;
    navigate(generatePath(route, { applicationUid }));
  }, [navigate, applicationUid, fromMissingDocumentation]);

  const handleSubmit = useCallback(
    async (values: AddCourseForm) => {
      if (!application.result) return;
      setSubmittingCourse(true);
      const { updatedAttachments, removedAttachments, newFiles } = values.attachments;
      const newUpdatedAttachments = updatedAttachments?.map((i) => {
        return { ...i, newType: AttachmentType.Course };
      });
      const fileUpdateParams = {
        application: application.result,
        attachmentGroup: attachmentGroupFormatted,
        qualificationUri: groupQualificationUri,
        updatedAttachments: newUpdatedAttachments,
        removedAttachments,
        newAttachments: newFiles.map((x) => ({
          file: x,
          type: AttachmentType.Course,
        })),
      };

      let newAttachmentTracker: SubmitAttachmentUpdatesTracker | null = null;
      let newAttachmentGroupTracker: CreateAttachmentGroupTracker | null = null;
      let group = attachmentGroupFormatted;
      if (!group) {
        // Create new attachment group
        const { tracker: attachmentGroupTracker } = await submitAttachmentGroup(
          {
            application: application.result,
            attachmentType: AttachmentType.Course,
            files: newFiles,
            simulatorResultItemUri: groupQualificationUri,
          },
          submitAttachmentGroupTracker
        );
        group =
          Object.values(attachmentGroupTracker?.addedAttachments || {})[0]?.result?.group || null;
        newAttachmentGroupTracker = attachmentGroupTracker;
      } else {
        // Submit attachments, when we already have an attachment group
        const { tracker: attachmentTracker } = await submitAttachments(
          fileUpdateParams,
          submitAttachmentsTracker
        );
        newAttachmentTracker = attachmentTracker;
      }
      setSubmittingCourse(false);

      if (newAttachmentTracker?.errors || newAttachmentGroupTracker?.errors) {
        newAttachmentGroupTracker && setSubmitAttachmentGroupTracker(newAttachmentGroupTracker);
        newAttachmentTracker && setSubmitAttachmentsTracker(newAttachmentTracker);
      } else {
        refresh?.();

        if (editingExistingQualification) {
          navigate(
            generatePath(routes.application.uploadMissingDocumentation, {
              applicationUid,
            })
          );
        } else {
          handleExit();
        }
      }
    },
    [
      application.result,
      submitAttachments,
      submitAttachmentGroup,
      attachmentGroupFormatted,
      groupQualificationUri,
      submitAttachmentsTracker,
      submitAttachmentGroupTracker,
      attachmentsForGroup,
      refresh,
      handleExit,
      applicationUid,
      editingExistingQualification,
      navigate,
    ]
  );

  const validation = useMemo(
    () =>
      Yup.object().shape({
        attachments: Yup.object().test('has-files', fm({ id: 'general.form.error' }), (v: any) => {
          if (!v) return attachmentsForGroup.length > 0;
          const changes = v as AttachmentGroupChanges;
          const resultingAttachmentCount =
            attachmentsForGroup.length +
            changes.newFiles.length -
            changes.removedAttachments.length;
          return resultingAttachmentCount > 0;
        }),
      }),
    [attachmentsForGroup.length, fm]
  );

  const initialValues = useMemo((): AddCourseForm => {
    return {
      attachments: { newFiles: [], removedAttachments: [], updatedAttachments: [] },
    };
  }, []);

  const handleActiveFileChange = useCallback(
    (file: Loadable<File> | null) => {
      if (file !== activeFile) {
        setActiveFile(file);
      }
    },
    [activeFile]
  );

  const isLoading = application.loading || submittingCourse;

  return (
    <div>
      <EditApplicationGuard>
        {isLoading ? (
          <Loader />
        ) : (
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validation}
          >
            {({ submitForm }) => (
              <Form noValidate>
                <NewApplicationLayout
                  hideHeading={editingExistingQualification}
                  exitLabel={fm({ id: 'general.form.back' })}
                  mainContentMaxWidth="100%"
                  canNavBack
                  canNavNext
                  navBackLabel={fm({ id: 'general.form.goback' })}
                  navNextLabel={fm({
                    id: editingExistingQualification ? 'general.form.save' : 'general.form.next',
                  })}
                  onNavNextClick={submitForm}
                  onNavBackClick={() => navigate(-1)}
                >
                  <CourseFormContent>
                    <div>
                      <CourseFormCard
                        heading={
                          qualification
                            ? `${qualification.title}: ${qualification.description}`
                            : fm({ id: 'addcourse.education.title' })
                        }
                      >
                        <Field
                          className="file-input"
                          name="attachments"
                          group={attachmentGroupFormatted}
                          component={FormAttachmentGroup}
                          onlyFileList
                          onChangeActiveFile={handleActiveFileChange}
                        />
                      </CourseFormCard>
                    </div>
                    <RightSection>
                      <>
                        {activeFile?.loading && <Loader />}
                        {activeFile?.error && <ErrorMessage error={activeFile.error} />}
                        {activeFile?.result && (
                          <FileViewer
                            file={activeFile.result}
                            handleDocumentError={(e) => setActiveFile({ error: e })}
                          />
                        )}
                      </>
                    </RightSection>
                  </CourseFormContent>
                </NewApplicationLayout>
              </Form>
            )}
          </Formik>
        )}
      </EditApplicationGuard>
    </div>
  );
};

const CourseFormContent = styled.div`
  display: grid;
  grid-template-rows: auto auto;
  grid-row-gap: 3rem;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 32px;
  margin-top: 3rem;
  justify-content: stretch;

  > * {
    position: relative;
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.large}) {
    grid-template-columns: 1fr;

    > div:first-child {
      min-height: 0;
    }

    > div:nth-child(2) {
      min-height: 500px;
    }
  }
`;

const RightSection = styled.div`
  max-height: 1000px;
  overflow: auto;
`;

const CourseFormCard = styled(Card)`
  min-height: calc(100vh - 440px);

  > .card__contents > * {
    margin-bottom: 32px;
  }

  .date-input {
    max-width: 344px;
  }

  .search-input {
    max-width: 416px;
  }
`;
